import { Helmet } from "react-helmet-async";
import { DEFAULT_SEO } from "./constants";

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: "website" | "article" | "product";
  noIndex?: boolean;
  keywords?: string[];
  author?: string;
}

/**
 * SEO - Manages page-level meta tags for SEO and social sharing
 * 
 * Usage:
 * ```tsx
 * <SEO 
 *   title="Virtual Try-On Studio"
 *   description="AI-powered virtual fitting room for your products"
 * />
 * ```
 */
export function SEO({
  title,
  description = DEFAULT_SEO.description,
  image = DEFAULT_SEO.image,
  url,
  type = "website",
  noIndex = false,
  keywords = DEFAULT_SEO.keywords,
  author = DEFAULT_SEO.siteName,
}: SEOProps) {
  const fullTitle = title 
    ? `${title} | ${DEFAULT_SEO.siteName}`
    : DEFAULT_SEO.title;
  
  const fullUrl = url || DEFAULT_SEO.url;
  const fullImage = image.startsWith("http") 
    ? image 
    : `${DEFAULT_SEO.url}${image}`;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords.join(", ")} />
      <meta name="author" content={author} />
      
      {/* Robots */}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:site_name" content={DEFAULT_SEO.siteName} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />
      <meta name="twitter:site" content={DEFAULT_SEO.twitterHandle} />

      {/* Canonical URL */}
      <link rel="canonical" href={fullUrl} />
    </Helmet>
  );
}

export default SEO;
